import request from 'services/axios'

export interface IMiniappAuthResponse {
    linked: boolean
    /** Which messenger the backend recognised from the payload signature. */
    platform?: 'bale' | 'telegram' | 'eitaa'
    token?: string
    requires_2fa?: boolean
    /**
     * Proof of this launch, reusable by the link step.
     *
     * Present when the messenger account is not yet bound. Exists because
     * Eitaa's verification is valid for 60 seconds and answers once per hash,
     * so `/link/` after the SMS login cannot re-check the launch payload.
     */
    link_ticket?: string
}

/**
 * Trade the signed launch payload for a session.
 *
 * Platform-agnostic on purpose: the backend works out which messenger this is
 * from the signature, so the client never has to sniff or claim one.
 *
 * `linked: false` is a normal first-run answer, not an error — the backend has
 * never seen this Bale account, so the caller falls back to the SMS login.
 */
export const miniappAuth = async (initData: string) => {
    const { data }: { data: IMiniappAuthResponse } = await request.post(
        '/miniapp/auth/',
        { init_data: initData }
    )
    return data
}

/**
 * Bind this Bale account to the member who just logged in with SMS, so every
 * later open of the mini app skips the login entirely.
 */
export const miniappLink = async (payload: {
    linkTicket?: string | null
    initData?: string | null
}) => {
    // Prefer the ticket: it is already-proven, costs the backend nothing, and
    // is the only thing that works on Eitaa (single-use verification).
    const body = payload.linkTicket
        ? { link_ticket: payload.linkTicket }
        : { init_data: payload.initData }
    const { data } = await request.post('/miniapp/link/', body)
    return data
}

/**
 * Sign in from a contact the user shared inside the mini app.
 *
 * Only meaningful on Eitaa/Telegram, whose `requestContact()` returns a
 * separately signed payload. The backend verifies that signature, so this is a
 * real login and not a claim — hence a token comes back and no SMS is needed.
 */
export const miniappContactLogin = async (
    initData: string,
    contactResponse: string
) => {
    const { data }: { data: IMiniappAuthResponse } = await request.post(
        '/miniapp/contact/',
        { init_data: initData, contact_response: contactResponse }
    )
    return data
}

export interface IOpenNotificationResponse {
    /** The PWA path the notification points at, or null if it is not ours. */
    path: string | null
    /** Which notification it was — analytics only. */
    kind?: string
}

/**
 * Trade a notification's token for the page it points at.
 *
 * A messenger message cannot carry a PWA path (a mini-app start param is
 * `[A-Za-z0-9_-]` only), so it carries a short token instead. The backend
 * answers only for the member the message was sent to, and the first call is
 * what marks the message as opened in the fund's history.
 */
/** A manager's invite code this launch came in with (`messaging/invites.py`). */
export const miniappReportInvite = async (code: string, platform: string) => {
    const { data }: { data: { recorded: boolean; joined: boolean } } =
        await request.post('/messaging/invite/open/', { code, platform })
    return data
}

export const miniappOpenNotification = async (token: string) => {
    const { data }: { data: IOpenNotificationResponse } = await request.post(
        '/messaging/open/',
        { token }
    )
    return data
}

/**
 * «صندوقم را منتقل کنید» from the قرعه‌کشی landing.
 *
 * Anonymous: the person asking has no Hamyan account yet, which is the entire
 * premise of the landing. If they *are* signed in the interceptor attaches the
 * token anyway and the backend prefers that over anything sent here — a number
 * we already hold beats one typed into a page.
 *
 * `contactResponse` is the messenger-signed `requestContact()` payload. Sending
 * it rather than the phone string it contains is the difference between a
 * number the messenger vouches for and a number the page claims; the backend
 * re-verifies the signature and records which of the two it got, so whoever
 * makes the call knows whether it was ever proven.
 */
export const requestFundTransfer = async (payload: {
    code?: string | null
    phone?: string | null
    name?: string | null
    initData?: string | null
    contactResponse?: string | null
}) => {
    const { data }: { data: { recorded: boolean; duplicate?: boolean } } =
        await request.post('/miniapp/transfer-request/', {
            code: payload.code || '',
            phone: payload.phone || '',
            name: payload.name || '',
            init_data: payload.initData || '',
            contact_response: payload.contactResponse || '',
        })
    return data
}
